import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Cpu, Upload, RefreshCw, AlertTriangle, CheckCircle, 
  Server, Activity, FileText, Users, Settings, Rocket,
  Download, Clock, Wifi, Battery
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportDashboard = () => {
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const headers = { Authorization: `Bearer ${token}` };
  
  const [showFirmwareDialog, setShowFirmwareDialog] = useState(false);
  const [newFirmware, setNewFirmware] = useState({ version: '', release_notes: '', file_url: '', file_hash: '' });
  const [rolloutVersion, setRolloutVersion] = useState('');
  const [rolloutPercentage, setRolloutPercentage] = useState(10);

  // Fetch all devices
  const { data: devices = [], isLoading: loadingDevices } = useQuery({
    queryKey: ['support-devices'],
    queryFn: async () => {
      const res = await axios.get(`${API}/devices`, { headers });
      return res.data;
    },
    enabled: !!token,
  });

  // Fetch firmware versions
  const { data: firmwareList = [], isLoading: loadingFirmware } = useQuery({
    queryKey: ['firmware-list'],
    queryFn: async () => {
      const res = await axios.get(`${API}/firmware`, { headers });
      return res.data;
    },
    enabled: !!token,
  });

  // Fetch webhook events
  const { data: webhookEvents = [], isLoading: loadingEvents } = useQuery({
    queryKey: ['webhook-events'],
    queryFn: async () => {
      const res = await axios.get(`${API}/webhooks/events?limit=20`, { headers });
      return res.data;
    },
    enabled: !!token,
  });

  // Upload firmware mutation
  const uploadFirmware = useMutation({
    mutationFn: async (firmware) => {
      return axios.post(`${API}/firmware`, firmware, { headers });
    },
    onSuccess: () => {
      toast.success('Firmware uploaded successfully!');
      queryClient.invalidateQueries(['firmware-list']);
      setShowFirmwareDialog(false);
      setNewFirmware({ version: '', release_notes: '', file_url: '', file_hash: '' });
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to upload firmware');
    }
  });

  // Start rollout mutation
  const startRollout = useMutation({
    mutationFn: async ({ version, percentage }) => {
      return axios.post(`${API}/firmware/${version}/rollout?percentage=${percentage}`, {}, { headers });
    },
    onSuccess: (res) => {
      toast.success(res.data.message);
      queryClient.invalidateQueries(['support-devices']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to start rollout');
    }
  });

  const handleUploadFirmware = () => {
    if (!newFirmware.version || !newFirmware.file_url || !newFirmware.file_hash) {
      toast.error('Please fill all required fields');
      return;
    }
    uploadFirmware.mutate(newFirmware);
  };

  const handleStartRollout = (version) => {
    startRollout.mutate({ version, percentage: rolloutPercentage });
  };

  // Stats
  const activeDevices = devices.filter(d => d.status === 'active').length;
  const offlineDevices = devices.filter(d => d.status === 'offline').length;
  const pendingUpdates = devices.filter(d => d.firmware_update_status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="support-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Support Dashboard</h2>
          <p className="text-slate-600 mt-1">Administrative tools for troubleshooting and firmware management</p>
        </div>
        <Badge className="bg-orange-500 text-white px-3 py-1 text-sm">
          {user?.role === 'support' ? 'Support Team' : 'Admin Access'}
        </Badge>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white/70 backdrop-blur-md border-white/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Server className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{activeDevices}</div>
                <div className="text-sm text-gray-500">Active Devices</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/70 backdrop-blur-md border-white/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{offlineDevices}</div>
                <div className="text-sm text-gray-500">Offline Devices</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/70 backdrop-blur-md border-white/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Upload className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{pendingUpdates}</div>
                <div className="text-sm text-gray-500">Pending Updates</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/70 backdrop-blur-md border-white/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Cpu className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{firmwareList.length}</div>
                <div className="text-sm text-gray-500">Firmware Versions</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="devices" className="space-y-6">
        <TabsList>
          <TabsTrigger value="devices" className="flex items-center gap-2">
            <Server className="w-4 h-4" />
            Devices
          </TabsTrigger>
          <TabsTrigger value="firmware" className="flex items-center gap-2">
            <Cpu className="w-4 h-4" />
            Firmware OTA
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Event Logs
          </TabsTrigger>
        </TabsList>

        {/* Devices Tab */}
        <TabsContent value="devices">
          <Card>
            <CardHeader>
              <CardTitle>Device Fleet</CardTitle>
              <CardDescription>All connected devices and their status</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingDevices ? (
                <div className="text-center py-8 text-gray-500">Loading devices...</div>
              ) : (
                <div className="space-y-3">
                  {devices.map((device) => (
                    <div key={device.device_id} className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-3 h-3 rounded-full ${device.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <div>
                          <div className="font-medium">{device.name}</div>
                          <div className="text-sm text-gray-500">{device.device_id}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Cpu className="w-4 h-4" />
                          v{device.firmware_version || '1.0.0'}
                        </div>
                        <div className="flex items-center gap-1 text-gray-600">
                          <Wifi className="w-4 h-4" />
                          {device.connectivity_type || 'LTE-M'}
                        </div>
                        <div className="flex items-center gap-1 text-gray-600">
                          <Battery className="w-4 h-4" />
                          {device.battery_level || 95}%
                        </div>
                        {device.firmware_update_status && (
                          <Badge className={device.firmware_update_status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}>
                            {device.firmware_update_status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Firmware Tab */}
        <TabsContent value="firmware">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Firmware Management</CardTitle>
                <CardDescription>Upload and deploy firmware updates</CardDescription>
              </div>
              <Button onClick={() => setShowFirmwareDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Firmware
              </Button>
            </CardHeader>
            <CardContent>
              {loadingFirmware ? (
                <div className="text-center py-8 text-gray-500">Loading firmware...</div>
              ) : firmwareList.length === 0 ? (
                <div className="text-center py-8">
                  <Cpu className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No firmware versions uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {firmwareList.map((fw) => (
                    <div key={fw.version} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Cpu className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-bold">Version {fw.version}</div>
                            <div className="text-sm text-gray-500">{fw.release_notes || 'No release notes'}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-100 text-green-700">
                            {fw.rollout_percentage || 0}% rolled out
                          </Badge>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={rolloutPercentage}
                            onChange={(e) => setRolloutPercentage(parseInt(e.target.value))}
                            className="w-20"
                          />
                          <Button 
                            size="sm"
                            onClick={() => handleStartRollout(fw.version)}
                            disabled={startRollout.isPending}
                          >
                            <Rocket className="w-4 h-4 mr-1" />
                            Deploy
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Event Logs</CardTitle>
              <CardDescription>Recent system events and notifications</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingEvents ? (
                <div className="text-center py-8 text-gray-500">Loading events...</div>
              ) : webhookEvents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No webhook events yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {webhookEvents.map((event) => (
                    <div key={event.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          event.event_type.includes('completed') ? 'bg-green-500' :
                          event.event_type.includes('alert') ? 'bg-red-500' : 'bg-blue-500'
                        }`}></div>
                        <div>
                          <div className="font-medium text-sm">{event.event_type}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(event.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {event.delivered_to?.length || 0} delivered
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Firmware Dialog */}
      <Dialog open={showFirmwareDialog} onOpenChange={setShowFirmwareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload New Firmware</DialogTitle>
            <DialogDescription>Add a new firmware version for OTA deployment</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Version *</Label>
              <Input 
                placeholder="e.g., 1.3.0"
                value={newFirmware.version}
                onChange={(e) => setNewFirmware({...newFirmware, version: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Release Notes</Label>
              <Textarea 
                placeholder="Describe the changes in this version..."
                value={newFirmware.release_notes}
                onChange={(e) => setNewFirmware({...newFirmware, release_notes: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Firmware URL *</Label>
              <Input 
                placeholder="https://firmware.smartpallet.io/v1.3.0.bin"
                value={newFirmware.file_url}
                onChange={(e) => setNewFirmware({...newFirmware, file_url: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>File Hash (SHA256) *</Label>
              <Input 
                placeholder="SHA256 hash for verification"
                value={newFirmware.file_hash}
                onChange={(e) => setNewFirmware({...newFirmware, file_hash: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFirmwareDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadFirmware} disabled={uploadFirmware.isPending}>
              {uploadFirmware.isPending ? 'Uploading...' : 'Upload Firmware'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportDashboard;
